import { TagIcon } from '@heroicons/react/24/outline';
import { MASTHEAD_ICON_BUTTON } from '../table-session/mastheadChrome';

/**
 * The masthead button that opens the category picker (feature 029, FR-026).
 *
 * <p><b>Why it costs no height.</b> It is built from the shared `MASTHEAD_ICON_BUTTON` around a
 * 20px heroicon — the same control the ⓘ beside it already uses, and already the tallest thing in
 * the masthead strip, which takes its height from its tallest control. A second one is therefore
 * free at every viewport, which is what SC-009 and the `@layout` battery measure. The frame's
 * `controls` slot was the obvious alternative and was rejected: it adds a whole padded row INSIDE
 * the viewport-locked frame, taken straight off the table.</p>
 *
 * <p>Unlike the ⓘ it is shown at EVERY width. The ⓘ is a phone stand-in for the status line and
 * disappears where the line is drawn; this is the only way to re-file a problem at any width.</p>
 *
 * <p>On a problem a Super Editor has locked (FR-048) it still opens: the picker then shows the
 * categories read-only and says who locked them. A disabled button could not tell a phone user why.
 * The accessible name carries the lock, since the masthead pill that shows it is `aria-hidden`.</p>
 */
export function ConceptualCategoriesControl({
  isOpen,
  onOpen,
  locked = false,
}: {
  isOpen: boolean;
  onOpen: () => void;
  locked?: boolean;
}) {
  const label = locked ? 'Categories for this problem (locked)' : 'Categories for this problem';

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      title={label}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      className={MASTHEAD_ICON_BUTTON + ' text-cream'}
      data-testid="conceptual-categories-button"
    >
      <TagIcon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
